import { apiSlice } from "./apiSlice";

// We need a separate base for unauthenticated public requests
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../helpers/apiUrl";

// Public (no-auth) slice for fetching the public website
export const websitePublicApi = createApi({
  reducerPath: "websitePublicApi",
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
  }),
  tagTypes: ["WebsitePublic"],
  endpoints: (builder) => ({
    getWebsiteSettings: builder.query({
      query: () => "/website/public/settings",
      providesTags: ["WebsitePublic"],
    }),
    getPublishedPages: builder.query({
      query: () => "/website/public/pages",
      providesTags: ["WebsitePublic"],
    }),
    getPublishedPage: builder.query({
      query: (slug) => `/website/public/pages/${slug}`,
      providesTags: (_result, _error, slug) => [{ type: "WebsitePublic", id: `page-${slug}` }],
    }),
    getPublishedPosts: builder.query({
      query: ({ page = 1, per_page = 10, category_id } = {}) => ({
        url: "/website/public/posts",
        params: { page, per_page, ...(category_id ? { category_id } : {}) },
      }),
      providesTags: ["WebsitePublic"],
    }),
    getPublishedPost: builder.query({
      query: (slug) => `/website/public/posts/${slug}`,
      providesTags: (_result, _error, slug) => [{ type: "WebsitePublic", id: `post-${slug}` }],
    }),
    getPublicCategories: builder.query({
      query: () => "/website/public/categories",
      providesTags: ["WebsitePublic"],
    }),
    getPostsByCategory: builder.query({
      query: ({ slug, page = 1, per_page = 10 }) => ({
        url: `/website/public/categories/${slug}/posts`,
        params: { page, per_page },
      }),
      providesTags: (_result, _error, { slug }) => [{ type: "WebsitePublic", id: `cat-${slug}` }],
    }),
  }),
});

export const {
  useGetWebsiteSettingsQuery,
  useGetPublishedPagesQuery,
  useGetPublishedPageQuery,
  useGetPublishedPostsQuery,
  useGetPublishedPostQuery,
  useGetPublicCategoriesQuery,
  useGetPostsByCategoryQuery,
} = websitePublicApi;

// ---------------------------------------------------------------------------
// Manage slice (authenticated)
// ---------------------------------------------------------------------------
const apiSliceWithTags = apiSlice.enhanceEndpoints({
  addTagTypes: ["WebsiteManage", "WebsiteCategories", "WebsiteDomain"],
});

export const websiteManageApiSlice = apiSliceWithTags.injectEndpoints({
  endpoints: (builder) => ({
    // Settings
    getWebsiteManageSettings: builder.query({
      query: () => "/website/manage/settings",
      providesTags: ["WebsiteManage"],
    }),
    updateWebsiteSettings: builder.mutation({
      query: (body) => ({
        url: "/website/manage/settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["WebsiteManage"],
    }),
    getWebsiteDomainSettings: builder.query({
      query: () => "/website/manage/domain",
      providesTags: ["WebsiteDomain"],
    }),
    updateWebsiteDomainSettings: builder.mutation({
      query: (body) => ({
        url: "/website/manage/domain",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["WebsiteDomain"],
    }),
    deleteWebsiteDomainSettings: builder.mutation({
      query: () => ({
        url: "/website/manage/domain",
        method: "DELETE",
      }),
      invalidatesTags: ["WebsiteDomain"],
    }),
    retryWebsiteDomainSettings: builder.mutation({
      query: () => ({
        url: "/website/manage/domain/retry",
        method: "POST",
      }),
      invalidatesTags: ["WebsiteDomain"],
    }),
    // Pages
    getCmsPages: builder.query({
      query: () => "/website/manage/pages",
      providesTags: ["WebsiteManage"],
    }),
    getCmsPage: builder.query({
      query: (id) => `/website/manage/pages/${id}`,
      providesTags: (_result, _error, id) => [{ type: "WebsiteManage", id }],
    }),
    createCmsPage: builder.mutation({
      query: (body) => ({
        url: "/website/manage/pages",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WebsiteManage"],
    }),
    updateCmsPage: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/website/manage/pages/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["WebsiteManage"],
    }),
    deleteCmsPage: builder.mutation({
      query: (id) => ({
        url: `/website/manage/pages/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["WebsiteManage"],
    }),
    // Posts
    getCmsPosts: builder.query({
      query: () => "/website/manage/posts",
      providesTags: ["WebsiteManage"],
    }),
    getCmsPost: builder.query({
      query: (id) => `/website/manage/posts/${id}`,
      providesTags: (_result, _error, id) => [{ type: "WebsiteManage", id }],
    }),
    createCmsPost: builder.mutation({
      query: (body) => ({
        url: "/website/manage/posts",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WebsiteManage"],
    }),
    updateCmsPost: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/website/manage/posts/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["WebsiteManage"],
    }),
    deleteCmsPost: builder.mutation({
      query: (id) => ({
        url: `/website/manage/posts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["WebsiteManage"],
    }),
    // Categories
    getCmsCategories: builder.query({
      query: () => "/website/manage/categories",
      providesTags: ["WebsiteCategories"],
    }),
    createCmsCategory: builder.mutation({
      query: (body) => ({
        url: "/website/manage/categories",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WebsiteCategories"],
    }),
    deleteCmsCategory: builder.mutation({
      query: (id) => ({
        url: `/website/manage/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["WebsiteCategories"],
    }),
  }),
});

export const {
  useGetWebsiteManageSettingsQuery,
  useUpdateWebsiteSettingsMutation,
  useGetWebsiteDomainSettingsQuery,
  useUpdateWebsiteDomainSettingsMutation,
  useDeleteWebsiteDomainSettingsMutation,
  useRetryWebsiteDomainSettingsMutation,
  useGetCmsPagesQuery,
  useGetCmsPageQuery,
  useCreateCmsPageMutation,
  useUpdateCmsPageMutation,
  useDeleteCmsPageMutation,
  useGetCmsPostsQuery,
  useGetCmsPostQuery,
  useCreateCmsPostMutation,
  useUpdateCmsPostMutation,
  useDeleteCmsPostMutation,
  useGetCmsCategoriesQuery,
  useCreateCmsCategoryMutation,
  useDeleteCmsCategoryMutation,
} = websiteManageApiSlice;
